/**
 * DashboardHero V7 — Cleaner teal gradient, toggle in header row.
 *
 * Layout (top to bottom):
 * 1. Header bar (gear · toggle · bell) — toggle replaces date
 * 2. KPI + Chart zone — crossfade between Art / Survival
 * 3. 36px bottom padding (overlap zone)
 *
 * V7 changes (from V6):
 * - Toggle moved into header bar, replacing date (reclaims ~35px)
 * - Pill ("Net this month") removed from hero — now rendered as
 *   NetSummaryCard in the card section by the parent page
 * - KPI/chart spacing loosened for calmer hierarchy
 */

// No local state — mode is controlled by parent
import { GlassCircleButton } from '@/components/ui/GlassCircleButton';
import { ArtChart, SurvivalChart } from '@/components/money/HeroAreaChart';
import { formatCurrency } from '@/lib/formatCurrency';

export type HeroMode = 'art' | 'survival';

const FONT = '"Manrope", "DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

export interface DashboardHeroProps {
  earned: number;              // cents — total income this month
  spent: number;               // cents — total art spend this month
  net: number;                 // cents — earned - spent  (passed through, not rendered in hero)
  burn: number;                // cents — monthly burn target (manual) or actual spend (plaid)
  burnSource?: 'plaid' | 'manual';
  dayOfMonth: number;
  daysInMonth: number;
  earnedTrend: number[];       // daily cumulative earned (dollars), length = daysInMonth
  spendTrend: number[];        // daily cumulative art spend (dollars), length = daysInMonth
  burnTrend: number[];         // daily cumulative all expenses (dollars), length = daysInMonth
  notificationCount: number;
  onSettingsPress: () => void;
  onNotificationsPress: () => void;
  onBurnTap?: () => void;
  mode: HeroMode;
  onModeChange?: (mode: HeroMode) => void;
  unreviewedSpendCount?: number;
  unreviewedEarnedCount?: number;
  onReviewSpendTap?: () => void;
  onReviewEarnedTap?: () => void;
}

/* ── Icons ── */
const GearIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
    stroke="rgba(255,255,255,0.8)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
  </svg>
);

const BellIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
    stroke="rgba(255,255,255,0.8)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 01-3.46 0" />
  </svg>
);

function formatDollars(cents: number): string {
  return formatCurrency(cents);
}

export function DashboardHero({
  earned,
  spent,
  net,
  burn,
  burnSource = 'manual',
  dayOfMonth,
  daysInMonth,
  earnedTrend,
  spendTrend,
  burnTrend,
  notificationCount,
  onSettingsPress,
  onNotificationsPress,
  onBurnTap,
  mode,
  onModeChange,
  unreviewedSpendCount = 0,
  unreviewedEarnedCount = 0,
  onReviewSpendTap,
  onReviewEarnedTap,
}: DashboardHeroProps) {
  const isArt = mode === 'art';

  const handleModeChange = (m: HeroMode) => {
    onModeChange?.(m);
  };

  // V7: date label removed — toggle now occupies center of header bar

  const crossfadeLayer = (visible: boolean): React.CSSProperties => ({
    position: 'absolute',
    inset: 0,
    opacity: visible ? 1 : 0,
    transition: `opacity 0.3s ${EASE}`,
    pointerEvents: visible ? 'auto' : 'none',
  });

  return (
    <div style={{
      background: 'linear-gradient(160deg, #2A9089 0%, #3DABA5 60%, #4dc4bd 100%)',
      paddingBottom: 36,
      overflow: 'hidden',
    }}>
      {/* ── 1. Header bar — toggle centered, replaces date ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'calc(env(safe-area-inset-top, 20px) + 10px) 20px 0',
      }}>
        <GlassCircleButton icon={GearIcon} onClick={onSettingsPress} ariaLabel="Settings" />
        <div style={{
          display: 'inline-flex',
          background: 'rgba(0,0,0,0.14)',
          borderRadius: 7,
          padding: 2,
        }}>
          {(['art', 'survival'] as const).map((m) => (
            <button
              key={m}
              onClick={() => handleModeChange(m)}
              style={{
                padding: '4px 11px',
                borderRadius: 6,
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
                background: mode === m ? '#3DABA5' : 'transparent',
                color: mode === m ? '#FFFFFF' : 'rgba(255,255,255,0.45)',
                fontSize: 11,
                fontWeight: mode === m ? 700 : 500,
                cursor: 'pointer',
                fontFamily: FONT,
                letterSpacing: '0.01em',
                transition: `all 0.22s ${EASE}`,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {m === 'art' ? 'My Art' : 'My Survival'}
            </button>
          ))}
        </div>
        <GlassCircleButton icon={BellIcon} onClick={onNotificationsPress} badge={notificationCount} ariaLabel="Notifications" />
      </div>

      {/* ── 2. KPI + Chart zone — crossfade, no pill ── */}
      {/* Fixed height required: both modes use position:absolute for crossfade,
          so the parent needs an explicit height to avoid collapsing to 0. */}
      <div style={{ height: 200, position: 'relative' }}>
        {/* Art mode */}
        <div style={crossfadeLayer(isArt)}>
          {/* KPIs — fixed height so chart starts at same Y in both modes */}
          <div style={{ display: 'flex', padding: '24px 20px 0', gap: 20, minHeight: 72 }}>
            <div>
              <div style={{
                fontSize: 10, fontWeight: 600, letterSpacing: '0.08em',
                textTransform: 'uppercase' as const, color: 'rgba(255,255,255,0.45)', fontFamily: FONT,
              }}>EARNED</div>
              <div style={{
                fontSize: 32, fontWeight: 800, color: 'white', letterSpacing: '-0.03em',
                fontFamily: FONT, lineHeight: 1, marginTop: 4,
              }}>
                {formatDollars(earned)}
              </div>
              {unreviewedEarnedCount > 0 && (
                <button onClick={onReviewEarnedTap} style={{
                  marginTop: 4, padding: '3px 10px', borderRadius: 8, border: 'none',
                  background: 'rgba(224,122,61,0.12)', color: '#92400E',
                  fontSize: 11, fontWeight: 600, fontFamily: FONT, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
                  {unreviewedEarnedCount} to review ›
                </button>
              )}
            </div>
            <div>
              <div style={{
                fontSize: 10, fontWeight: 600, letterSpacing: '0.08em',
                textTransform: 'uppercase' as const, color: 'rgba(255,255,255,0.45)', fontFamily: FONT,
              }}>SPENT ON ART</div>
              <div style={{
                fontSize: 32, fontWeight: 800, color: 'white', letterSpacing: '-0.03em',
                fontFamily: FONT, lineHeight: 1, marginTop: 4, opacity: 0.75,
              }}>
                {formatDollars(spent)}
              </div>
              {unreviewedSpendCount > 0 && (
                <button onClick={onReviewSpendTap} style={{
                  marginTop: 4, padding: '3px 10px', borderRadius: 8, border: 'none',
                  background: 'rgba(224,122,61,0.12)', color: '#92400E',
                  fontSize: 11, fontWeight: 600, fontFamily: FONT, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
                  {unreviewedSpendCount} to review ›
                </button>
              )}
            </div>
          </div>
          {/* Art chart */}
          <div style={{ padding: '20px 0 0' }}>
            <ArtChart earnData={earnedTrend} spendData={spendTrend} dayOfMonth={dayOfMonth} />
          </div>
        </div>

        {/* Survival mode */}
        <div style={crossfadeLayer(!isArt)}>
          {/* KPI — fixed height matches Art mode so chart starts at same Y */}
          <div style={{ padding: '24px 20px 0', height: 72 }}>
            <div style={{
              fontSize: 10, fontWeight: 600, letterSpacing: '0.08em',
              textTransform: 'uppercase' as const, color: 'rgba(255,255,255,0.45)', fontFamily: FONT,
            }}>EARNED TOWARD BURN</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
              <div style={{
                fontSize: 32, fontWeight: 800, color: 'white', letterSpacing: '-0.03em',
                fontFamily: FONT, lineHeight: 1,
              }}>
                {formatDollars(earned)}
              </div>
              <span style={{
                fontSize: 18, fontWeight: 600, color: 'rgba(255,255,255,0.40)', fontFamily: FONT, lineHeight: 1,
              }}>
                {burn > 0 ? `/ ${formatDollars(burn)}` : '/ —'}
              </span>
              {burnSource === 'plaid' && burn > 0 && (
                <span style={{
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                  color: '#fff', background: 'rgba(255,255,255,0.18)',
                  padding: '2px 6px', borderRadius: 4, marginLeft: 6,
                  letterSpacing: '0.02em',
                }}>tracked</span>
              )}
            </div>
          </div>
          {/* Survival chart */}
          <div style={{ padding: '20px 0 0' }}>
            <SurvivalChart earnData={earnedTrend} burnLine={burnTrend} dayOfMonth={dayOfMonth} />
          </div>
        </div>
      </div>
    </div>
  );
}
